import { Box, Typography } from '@mui/material';
import { TableV2Column } from 'components/Table/TableV2';
import {
  CandidateBatchGroup,
  CandidateBatchStatus,
  SearchableCandidateBatchSortableFields
} from 'generated-graphql/types';
import { Paths } from 'services/navigation/paths';
import { SearchCandidateBatchesItem } from '../types/SearchCandidateBatchesItem';
import { AnchorText } from 'components/Text';
import { RouterLink } from 'common/ui/RouterLink';
import {
  ActionPopover,
  ActionOption
} from 'components/ActionPopover/ActionPopover';
import { formatDateFromNowAbbreviated } from 'utils/datetime';
import { SourcingAnalyseColumnButton } from 'modules/analyse/components/SourcingAnalyseColumnButton';
import { sourcingGetAnalyseInviteColumn } from 'modules/analyse/utils/sourcingGetAnalyseInviteColumn';
import { CandidateBatchStatusChip } from 'modules/analyse/components/CandidateBatchStatusChip';
import { statusFilterOptions, STATUS_FILTER_ID } from '../constants/filters';
import { getAnalysisFilterLabel } from './getAnalysisFilterLabel';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

type GetAnalysesColumnsProps = {
  sourcingEnabled: boolean;
  internalSourcingEnabled: boolean;
  handleArchiveConfirmation: (candidateBatchId: string) => void;
  handleApplyRuleset: (
    candidateBatchIds: string[],
    initialCandidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>,
    status?: CandidateBatchStatus
  ) => void;
  handleEditExternalIdClick: (
    candidateBatchId: string,
    externalId?: string | null
  ) => void;
  track: (event: string, properties?: Record<string, any>) => void;
  isEditExternalIdEnabled: boolean | undefined;
  handleStartAnalysis: (candidateBatchId: string) => void;
};

export const getAnalysesColumns = ({
  sourcingEnabled,
  internalSourcingEnabled,
  handleArchiveConfirmation,
  handleApplyRuleset,
  handleEditExternalIdClick,
  track,
  isEditExternalIdEnabled,
  handleStartAnalysis
}: GetAnalysesColumnsProps): TableV2Column<SearchCandidateBatchesItem>[] => [
  {
    name: 'Job title',
    cell: (row) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <RouterLink
          to={
            row.status === CandidateBatchStatus.Draft
              ? `${Paths.ANALYSE_CREATE}/${row.id}`
              : `${Paths.ANALYSE}/${row.id}`
          }
        >
          <AnchorText>{getAnalysisTitle(row, row.id)}</AnchorText>
        </RouterLink>
      </Box>
    )
  },
  {
    name: 'Location',
    selector: (row) => row.jobDescriptionParsed?.location || '-',
    width: '120px'
  },
  {
    name: 'Status',
    cell: (row) => <CandidateBatchStatusChip status={row.status} />,
    width: '120px',
    filterable: true,
    filterId: STATUS_FILTER_ID,
    filterOptions: statusFilterOptions.map((status) => ({
      value: status,
      label: getAnalysisFilterLabel(status)
    }))
  },
  {
    id: SearchableCandidateBatchSortableFields.CandidatesCount,
    name: 'Applied',
    cell: (row) => (
      <SourcingAnalyseColumnButton
        candidateBatchId={row.id}
        candidatesCount={row.candidatesCount}
        status={row.status}
      />
    ),
    sortable: true,
    sortField: SearchableCandidateBatchSortableFields.CandidatesCount,
    width: '110px'
  },
  {
    name: 'Job ref',
    cell: (row) => row.atsJobReference || '-',
    width: '100px'
  },
  {
    name: 'Job ID',
    cell: (row) => row.atsJobId || '-',
    width: '100px'
  },
  {
    name: 'Owner',
    cell: (row) => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography>{row.user?.name}</Typography>
      </Box>
    ),
    width: '150px'
  },
  {
    id: SearchableCandidateBatchSortableFields.UpdatedAt,
    name: 'Updated',
    cell: (row) => formatDateFromNowAbbreviated(row.updatedAt),
    sortField: SearchableCandidateBatchSortableFields.UpdatedAt,
    sortable: true,
    width: '120px'
  },
  {
    id: SearchableCandidateBatchSortableFields.ExternalId,
    name: 'External ID',
    cell: (row) => row.externalId || '',
    sortable: true,
    sortField: SearchableCandidateBatchSortableFields.ExternalId,
    width: '150px'
  },
  {
    isAction: true,
    cell: (row) => {
      // The workflow builder owns a workflow-managed batch, so Classic only reads it.
      if (row.isWorkflowManaged) return null;

      const options: ActionOption[] = [
        ...sourcingGetAnalyseInviteColumn({
          candidateBatchId: row.id,
          isDraft: row.status === CandidateBatchStatus.Draft,
          sourcingEnabled,
          internalSourcingEnabled,
          track
        }),
        ...(row.status === CandidateBatchStatus.Draft
          ? [
              {
                label: 'Start analysis',
                onClick: () => {
                  handleStartAnalysis(row.id);
                }
              }
            ]
          : []),
        ...(row.status !== CandidateBatchStatus.Draft
          ? [
              {
                label: 'Apply ruleset',
                onClick: () =>
                  handleApplyRuleset(
                    [row.id],
                    {
                      id: row.candidateBatchGroup?.id ?? '',
                      name: row.candidateBatchGroup?.name ?? ''
                    },
                    row.status
                  )
              }
            ]
          : []),
        ...(isEditExternalIdEnabled
          ? [
              {
                label: 'Edit ID',
                onClick: () => handleEditExternalIdClick(row.id, row.externalId)
              }
            ]
          : []),
        ...(row.status !== CandidateBatchStatus.Archived &&
        row.status !== CandidateBatchStatus.Paused
          ? [
              {
                label: 'Archive',
                onClick: () => handleArchiveConfirmation(row.id),
                itemColor: 'error.main'
              }
            ]
          : [])
      ];

      return <ActionPopover id={row.id} options={options} />;
    },
    width: '10px'
  }
];
